import { signedFetch } from '../../super-picker/services/api';

type PublicPsfSession = { uuid: string; sessionToken: string } | null;
let publicPsfSession: PublicPsfSession = null;
let publicPsfExpected = false;

const PUBLIC_PSF_SESSION_PREFIX = 'public-psf-event-session:';
const RAW_API_URL = process.env.REACT_APP_API_URL || '/';
const API_ROOT = RAW_API_URL.replace(/\/+$/, '');
const USE_HOSTING_PROXY = API_ROOT === '' || API_ROOT === '/';
const FUNCTION_BASE = USE_HOSTING_PROXY ? '' : (API_ROOT.endsWith('/api') ? API_ROOT : `${API_ROOT}/api`);
const PUBLIC_API_BASE = USE_HOSTING_PROXY ? '/api' : `${FUNCTION_BASE}/api`;

/** Build a public Personalized PSF route using the shared Super Picker API base. */
export function publicPsfApiUrl(route: string): string {
  const normalizedRoute = route.startsWith('/api/') ? route.slice('/api'.length) : route;
  return `${PUBLIC_API_BASE}${normalizedRoute.startsWith('/') ? normalizedRoute : `/${normalizedRoute}`}`;
}

/**
 * Free ngrok accounts may return an interstitial HTML page to browser
 * requests. Add ngrok's documented bypass flag only when this SPA is actually
 * being served from an ngrok hostname; production and localhost requests are
 * left unchanged.
 */
export function withNgrokBrowserBypass(input: string): string {
  if (typeof window === 'undefined') return input;
  const browserHostname = window.location.hostname.toLowerCase();
  let targetHostname = '';
  try { targetHostname = new URL(input, window.location.origin).hostname.toLowerCase(); } catch { /* relative URL */ }
  const isNgrok = (hostname: string) => /(^|\.)ngrok(?:-free)?\.(?:dev|app|io)$/.test(hostname);
  if (!isNgrok(browserHostname) && !isNgrok(targetHostname)) return input;
  return `${input}${input.includes('?') ? '&' : '?'}ngrok-skip-browser-warning=true`;
}

export function withNgrokBrowserBypassHeaders(init?: HeadersInit): Headers {
  const headers = new Headers(init);
  if (typeof window !== 'undefined') {
    const browserHostname = window.location.hostname.toLowerCase();
    const directHostname = (() => {
      try { return new URL(PUBLIC_API_BASE, window.location.origin).hostname.toLowerCase(); } catch { return ''; }
    })();
    if (/(^|\.)ngrok(?:-free)?\.(?:dev|app|io)$/.test(browserHostname) || /(^|\.)ngrok(?:-free)?\.(?:dev|app|io)$/.test(directHostname)) {
      headers.set('ngrok-skip-browser-warning', 'true');
    }
  }
  return headers;
}

/** Re-anchor the public event clock whenever a public API response provides it. */
export function trackPublicPsfResponse(response: Response): Response {
  const headerServerNow = response.headers.get('X-Public-Server-Now');
  if (headerServerNow) {
    window.dispatchEvent(new CustomEvent('public-psf-server-time', { detail: { serverNow: headerServerNow } }));
  }
  response.clone().json().then((payload: any) => {
    if (payload && typeof payload.serverNow === 'string') {
      window.dispatchEvent(new CustomEvent('public-psf-server-time', { detail: { serverNow: payload.serverNow } }));
    }
  }).catch(() => undefined);
  return response;
}

function localStorageKey(uuid: string): string {
  return `${PUBLIC_PSF_SESSION_PREFIX}${uuid}`;
}

function persistPublicPsfSession(session: PublicPsfSession): void {
  try {
    if (!session) return;
    localStorage.setItem(localStorageKey(session.uuid), JSON.stringify(session));
    // Remove the old tab-scoped copy after promoting it to persistent storage.
    sessionStorage.removeItem(localStorageKey(session.uuid));
  } catch {
    // Session storage may be unavailable in privacy-restricted browsers.
  }
}

export function configurePublicPsfSession(session: PublicPsfSession): void {
  if (!session) {
    clearPublicPsfSession();
    publicPsfExpected = false;
    return;
  }
  publicPsfSession = session;
  publicPsfExpected = true;
  persistPublicPsfSession(session);
}

/** Mark the current page as an event-scoped public page before restoring OTP state. */
export function setPublicPsfContext(expected: boolean): void {
  publicPsfExpected = expected;
  if (!expected) publicPsfSession = null;
}

export function restorePublicPsfSession(uuid: string): PublicPsfSession | null {
  publicPsfExpected = true;
  try {
    const key = localStorageKey(uuid);
    let raw = localStorage.getItem(key);
    if (!raw) {
      raw = sessionStorage.getItem(key);
      if (raw) localStorage.setItem(key, raw);
    }
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { uuid?: unknown; sessionToken?: unknown } | null;
    if (!parsed || parsed.uuid !== uuid || typeof parsed.sessionToken !== 'string' || !parsed.sessionToken) return null;
    const session = { uuid, sessionToken: parsed.sessionToken };
    publicPsfSession = session;
    return session;
  } catch {
    return null;
  }
}

export function clearPublicPsfSession(uuid?: string): void {
  const keyUuid = uuid || publicPsfSession?.uuid;
  publicPsfSession = null;
  if (keyUuid) {
    try {
      localStorage.removeItem(localStorageKey(keyUuid));
      sessionStorage.removeItem(localStorageKey(keyUuid));
    } catch { /* ignore storage failures */ }
  }
}

function psfFetch(input: string, init?: RequestInit): Promise<Response> {
  if (!input.startsWith('/api/super-psf/')) return signedFetch(input, init);
  if (publicPsfExpected && !publicPsfSession) {
    window.dispatchEvent(new CustomEvent('public-psf-session-required'));
    return Promise.resolve(new Response(JSON.stringify({ error: 'Verification required', code: 'PUBLIC_SESSION_REQUIRED' }), {
      status: 401,
      headers: { 'Content-Type': 'application/json' },
    }));
  }
  if (!publicPsfSession) return signedFetch(input, init);
  const target = input.slice('/api/super-psf'.length);
  const headers = withNgrokBrowserBypassHeaders(init?.headers);
  headers.set('x-event-session', publicPsfSession.sessionToken);
  return fetch(withNgrokBrowserBypass(publicPsfApiUrl(`/api/super-personalized-psf-public/share/${encodeURIComponent(publicPsfSession.uuid)}/tool${target}`)), {
    ...init,
    headers,
    credentials: 'omit',
  }).then(async (rawResponse) => {
    const response = trackPublicPsfResponse(rawResponse);
    if (response.status === 401 || response.status === 403 || response.status === 410) {
      const payload = await response.clone().json().catch(() => ({}));
      if (payload?.code === 'EVENT_NOT_STARTED' || payload?.code === 'EVENT_EXPIRED') {
        window.dispatchEvent(new CustomEvent('public-psf-event-status', { detail: { code: payload.code } }));
      }
      if (payload?.code === 'PUBLIC_SESSION_REQUIRED' || payload?.code === 'SHARE_SESSION_REQUIRED') {
        window.dispatchEvent(new CustomEvent('public-psf-session-required'));
      }
    }
    return response;
  });
}

function publicFilterHistoryFetch(init: RequestInit): Promise<Response> {
  if (publicPsfExpected && !publicPsfSession) {
    window.dispatchEvent(new CustomEvent('public-psf-session-required'));
    return Promise.resolve(new Response(JSON.stringify({ error: 'Verification required', code: 'PUBLIC_SESSION_REQUIRED' }), {
      status: 401,
      headers: { 'Content-Type': 'application/json' },
    }));
  }
  if (!publicPsfSession) return signedFetch('/api/super-psf/filter-applications', init);
  const headers = withNgrokBrowserBypassHeaders(init.headers);
  headers.set('x-event-session', publicPsfSession.sessionToken);
  return fetch(withNgrokBrowserBypass(publicPsfApiUrl(`/api/super-personalized-psf-public/share/${encodeURIComponent(publicPsfSession.uuid)}/filters`)), {
    ...init,
    headers,
    credentials: 'omit',
  }).then(trackPublicPsfResponse);
}

export interface Transaction {
  transactionId: number;
  transactionProjectName: string;
  transactionAddress: string;
  transactionBlockStreet: string;
  transactionFloor: number | null;
  transactionUnitNum: string | null;
  transactionAreaSqft: number | null;
  transactionPrice: number | null;
  transactionPsf: number | null;
  transactionDate: string;
  transactionSubtype: string | null;
  transactionTenure: string | null;
  transactionTenureDate: string | null;
  transactionCompletionYear: number | null;
  transactionSaleType: string | null;
  transactionBedroom: number | null;
  transactionPostalDistrict: string | null;
  transactionPlanArea: string | null;
  transactionRegion: string | null;
  avgProjectPsf: number | null;
  unitCount: number | null;
  aa: number | null;
  ry: number | null;
  ryPlusAa: number | null;
  distanceFromReferenceKm: number | null;
}

export interface TransactionsResponse {
  data: Transaction[];
  total: number;
  page: number;
  pageSize: number;
  startDate: string;
  endDate: string;
  distinctValues?: Record<string, string[]>;
}

export interface ProjectSummary {
  projectName: string;
  txnCount: number;
  avgPsf: number | null;
  minPsf: number | null;
  maxPsf: number | null;
  avgPrice: number | null;
  avgAreaSqft: number | null;
  lastTransactionDate: string | null;
  tenure: string | null;
  tenureStartDate: string | null;
  completionYear: number | null;
  district: string | null;
  planArea: string | null;
  region: string | null;
  subtype: string | null;
  nearestPriSch: string | null;
  nearestPriSchDistance: number | null;
  nearestMrt: string | null;
  nearestMrtDistance: number | null;
  unitCount: number | null;
  aa: number | null;
  ry: number | null;
  ryPlusAa: number | null;
  distanceFromReferenceKm: number | null;
}

export interface ProjectsResponse {
  data: ProjectSummary[];
  total: number;
  startDate: string;
  endDate: string;
}

export type PropertyTypeFilter = 'all' | 'condo' | 'landed';
export type TenureGroupFilter = 'all' | 'leasehold' | 'freehold';

export interface MarketFilterParams {
  period?: string;                 // 1Y | 2Y | 5Y | 10Y
  startDate?: string;
  endDate?: string;

  propertyType?: PropertyTypeFilter;
  tenureGroup?: TenureGroupFilter;
  excludeExecutiveCondominiums?: boolean;

  saleTypes?: string[];            // New Sale | Resale | Sub Sale

  bedroomMin?: number;
  bedroomMax?: number;

  priceMin?: number;
  priceMax?: number;

  psfMin?: number;
  psfMax?: number;

  areaMin?: number;
  areaMax?: number;

  floorMin?: number;
  floorMax?: number;

  projects?: string[];
  districts?: string[];
  planAreas?: string[];
  regions?: string[];

  avgProjectPsfMin?: number;
  avgProjectPsfMax?: number;

  referenceLatitude?: number;
  referenceLongitude?: number;
  radiusKm?: number;

  filters?: Record<string, string>;
}

function appendMarketParams(
  q: URLSearchParams,
  params: MarketFilterParams,
) {
  if (params.period) q.set('period', params.period);
  if (params.startDate) q.set('startDate', params.startDate);
  if (params.endDate) q.set('endDate', params.endDate);

  if (
    params.propertyType &&
    params.propertyType !== 'all'
  ) {
    q.set('propertyType', params.propertyType);
  }

  if (
    params.tenureGroup &&
    params.tenureGroup !== 'all'
  ) {
    q.set('tenureGroup', params.tenureGroup);
  }
  if (params.excludeExecutiveCondominiums) q.set('excludeExecutiveCondominiums', 'true');

  if (
    params.saleTypes &&
    params.saleTypes.length > 0
  ) {
    q.set(
      'saleTypes',
      params.saleTypes.join(','),
    );
  }

  (
    ['bedroom', 'price', 'psf', 'area', 'floor'] as const
  ).forEach((key) => {
    const min =
      params[
        `${key}Min` as keyof MarketFilterParams
      ] as number | undefined;

    const max =
      params[
        `${key}Max` as keyof MarketFilterParams
      ] as number | undefined;

    if (min != null) {
      q.set(`${key}Min`, String(min));
    }

    if (max != null) {
      q.set(`${key}Max`, String(max));
    }
  });

  // NEW — Projects
  if (
    params.projects &&
    params.projects.length > 0
  ) {
    q.set(
      'projects',
      params.projects.join(','),
    );
  }

  // NEW — Regions
  if (
    params.regions &&
    params.regions.length > 0
  ) {
    q.set(
      'regions',
      params.regions.join(','),
    );
  }

  if (params.districts?.length) q.set('districts', params.districts.join(','));
  if (params.planAreas?.length) q.set('planAreas', params.planAreas.join(','));

  // NEW — Avg Project PSF
  if (params.avgProjectPsfMin != null) {
    q.set(
      'avgProjectPsfMin',
      String(params.avgProjectPsfMin),
    );
  }

  if (params.avgProjectPsfMax != null) {
    q.set(
      'avgProjectPsfMax',
      String(params.avgProjectPsfMax),
    );
  }

  if (
    Number.isFinite(params.referenceLatitude)
    && Number.isFinite(params.referenceLongitude)
    && Number.isFinite(params.radiusKm)
    && Number(params.radiusKm) > 0
  ) {
    q.set('referenceLatitude', String(params.referenceLatitude));
    q.set('referenceLongitude', String(params.referenceLongitude));
    q.set('radiusKm', String(params.radiusKm));
  }

  if (
    params.filters &&
    Object.keys(params.filters).length > 0
  ) {
    q.set(
      'filters',
      JSON.stringify(params.filters),
    );
  }
}

async function parseOrThrow(res: Response, fallback: string) {
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const message = err?.detail
      ? `${err?.error || fallback}: ${err.detail}`
      : (err?.error || fallback);
    throw Object.assign(new Error(message), { code: err?.code, detail: err?.detail });
  }
  return res.json();
}

// React StrictMode intentionally replays mount effects in development. Keep
// identical GETs in flight only once so the replay shares the same response
// instead of issuing duplicate backend/database work. The entry is removed
// after completion, so this is request deduplication rather than stale data
// caching.
const inFlightJson = new Map<string, Promise<unknown>>();
function dedupedJson<T>(url: string, fallback: string): Promise<T> {
  const existing = inFlightJson.get(url);
  if (existing) return existing as Promise<T>;
  const request = psfFetch(url)
    .then((res) => parseOrThrow(res, fallback))
    .finally(() => inFlightJson.delete(url));
  inFlightJson.set(url, request);
  return request as Promise<T>;
}

export async function fetchTransactions(params: MarketFilterParams & {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: string;
  includeDistincts?: boolean;
  headerMulti?: Record<string, string[]>;
  headerRanges?: Record<string, { min: string; max: string }>;
  beforeDate?: string;
  afterDate?: string;
}): Promise<TransactionsResponse> {
  const q = new URLSearchParams();
  appendMarketParams(q, params);
  if (params.page) q.set('page', String(params.page));
  if (params.pageSize) q.set('pageSize', String(params.pageSize));
  if (params.sortBy) q.set('sortBy', params.sortBy);
  if (params.sortDir) q.set('sortDir', params.sortDir);
  if (params.beforeDate) q.set('beforeDate', params.beforeDate);
  if (params.afterDate) q.set('afterDate', params.afterDate);
  if (params.includeDistincts) q.set('includeDistincts', '1');
  if (params.headerMulti && Object.values(params.headerMulti).some((values) => values.length > 0)) {
    q.set('headerMulti', JSON.stringify(params.headerMulti));
  }
  if (params.headerRanges && Object.values(params.headerRanges).some((range) => range.min !== '' || range.max !== '')) {
    q.set('headerRanges', JSON.stringify(params.headerRanges));
  }
  return dedupedJson<TransactionsResponse>(`/api/super-psf/transactions?${q.toString()}`, 'Failed to load transactions');
}

export async function fetchProjects(params: MarketFilterParams & { limit?: number }): Promise<ProjectsResponse> {
  const q = new URLSearchParams();
  appendMarketParams(q, params);
  if (params.limit) q.set('limit', String(params.limit));
  return dedupedJson<ProjectsResponse>(`/api/super-psf/projects?${q.toString()}`, 'Failed to load projects');
}

export interface OtherMethodSubmission {
  remarks: string;
  tenure: 'leasehold' | 'freehold';
}

export async function submitOtherMethod(body: OtherMethodSubmission): Promise<{ ok: boolean; id: number }> {
  const payload = {
    remarks: body.remarks.trim(),
    tenure: body.tenure.toUpperCase(),
  };
  if (!payload.remarks) throw new Error('Remarks are required');
  if (payload.tenure !== 'LEASEHOLD' && payload.tenure !== 'FREEHOLD') {
    throw new Error('Please select a valid tenure');
  }
  if (publicPsfExpected && !publicPsfSession) {
    throw Object.assign(new Error('Verification required'), { code: 'PUBLIC_SESSION_REQUIRED' });
  }
  const res = publicPsfSession
    ? trackPublicPsfResponse(await fetch(withNgrokBrowserBypass(publicPsfApiUrl(`/api/super-personalized-psf-public/share/${encodeURIComponent(publicPsfSession.uuid)}/other-methods`)), {
      method: 'POST',
      headers: withNgrokBrowserBypassHeaders({ 'Content-Type': 'application/json', 'x-event-session': publicPsfSession.sessionToken }),
      body: JSON.stringify(payload),
      credentials: 'omit',
    }))
    : await signedFetch('/api/super-psf/other-methods', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  return parseOrThrow(res, 'Failed to save your submission');
}

export async function exportPsfPdf(payload: {
  reportTitle?: string; viewLabel?: string; coverSubject?: string; consumerName?: string | null;
  userInfo?: { Name?: string; Mobile?: string; Email?: string; CEANumber?: string; Photo?: string } | null;
  columns: string[]; rows: string[][]; filterSummary?: string[];
  resultSummary?: string[]; calculationSummary?: string[]; filename?: string; [key: string]: unknown;
}): Promise<void> {
  const res = await psfFetch('/api/super-psf/export-pdf', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error || 'Failed to generate PDF');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a'); link.href = url;
  link.download = payload.filename || 'project-research.pdf'; link.click();
  URL.revokeObjectURL(url);
}

export interface ReturnMetricsResponse {
  project: string;
  metrics: {
    aa: number | null;
    ry: number | null;
    ryPlusAa: number | null;
    diagnostics?: Record<string, unknown>;
  };
}

/** Auditable AA/RY inputs for investigating a project’s return metrics. */
export async function fetchReturnMetrics(project: string): Promise<ReturnMetricsResponse> {
  const q = new URLSearchParams({ project });
  const res = await psfFetch(`/api/super-psf/return-metrics?${q.toString()}`);
  return parseOrThrow(res, 'Failed to calculate return metrics');
}

export interface NewLaunchUnit {
  id: number;
  projectName: string;
  blockNumber: string | null;
  unitNumber: string | null;
  floor: number | null;
  bedroom: number | null;
  bathroom: number | null;
  areaSqft: number | null;
  price: number | null;
  psf: number;
  unitTypeDescription: string | null;
  tenure: string | null;
  topDate: number | null;
  district: string | null;
  area: string | null;
  region: string | null;
}

export interface NewLaunchUnitsResponse {
  units: NewLaunchUnit[];
  total: number;
  projectCount: number;
  psfMax: number;
  psfMin?: number;
}

export interface NewLaunchUnitFilters {
  bedroomMin?: number;
  bedroomMax?: number;
  areaMin?: number;
  areaMax?: number;
  excludeExecutiveCondominiums?: boolean;
}

export async function fetchNewLaunchUnits(params: NewLaunchUnitFilters & {
  psfMax: number;
  psfMin?: number;
  limit?: number;
  allResults?: boolean;
  tenureGroup?: 'leasehold' | 'freehold';
}): Promise<NewLaunchUnitsResponse> {
  const q = new URLSearchParams();
  q.set('psfMax', String(params.psfMax));
  if (params.psfMin != null) q.set('psfMin', String(params.psfMin));
  if (params.limit) q.set('limit', String(params.limit));
  if (params.allResults) q.set('all', 'true');
  if (params.tenureGroup) q.set('tenureGroup', params.tenureGroup);
  if (params.excludeExecutiveCondominiums) q.set('excludeExecutiveCondominiums', 'true');
  (['bedroomMin', 'bedroomMax', 'areaMin', 'areaMax'] as const).forEach((key) => {
    if (params[key] != null) q.set(key, String(params[key]));
  });
  const res = await psfFetch(`/api/super-psf/new-launch-units?${q.toString()}`);
  return parseOrThrow(res, 'Failed to load new launch units');
}

// ── Consumer Events ─────────────────────────────────────────────────────────

export interface ConsumerEvent {
  id: number;
  name: string;
  description: string | null;
  venue: string | null;
  startAt: string;
  endAt: string | null;
  capacity: number | null;
  createdBy: string;
  canManage: boolean;
  inviteCount: number;
  myInviteCount: number;
}

export interface ConsumerEventInvite {
  id: number;
  name: string;
  email: string;
  mobile: string;
  invitedBy: string;
  createdAt: string;
  invitationSentAt: string | null;
  hasPersonalizedLink: boolean;
  personalizedLinkSentAt: string | null;
}

export async function fetchConsumerEvents(): Promise<{ data: ConsumerEvent[]; isAdmin: boolean }> {
  const res = await signedFetch('/api/super-psf/events');
  return parseOrThrow(res, 'Failed to load events');
}

export async function createConsumerEvent(body: {
  name: string; description?: string; venue?: string;
  startAt: string; endAt?: string | null; capacity?: number | null;
}): Promise<{ id: number }> {
  const res = await signedFetch('/api/super-psf/events', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return parseOrThrow(res, 'Failed to create event');
}

export async function updateConsumerEvent(id: number, body: {
  name?: string; description?: string; venue?: string;
  startAt?: string; endAt?: string | null; capacity?: number | null;
}): Promise<{ ok: boolean }> {
  const res = await signedFetch(`/api/super-psf/events/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return parseOrThrow(res, 'Failed to update event');
}

export async function deleteConsumerEvent(id: number): Promise<{ ok: boolean }> {
  const res = await signedFetch(`/api/super-psf/events/${id}`, { method: 'DELETE' });
  return parseOrThrow(res, 'Failed to delete event');
}

export async function fetchEventInvites(eventId: number): Promise<{ data: ConsumerEventInvite[] }> {
  const res = await signedFetch(`/api/super-psf/events/${eventId}/invites`);
  return parseOrThrow(res, 'Failed to load invites');
}

export async function addEventInvites(
  eventId: number,
  invites: Array<{ name: string; email: string; mobile: string }>,
): Promise<{ ok: boolean; created: number; emailResults?: Array<{ inviteId: number; email: string; status: 'sent' | 'skipped' | 'failed'; error?: string; invitationSentAt: string | null }> }> {
  const res = await signedFetch(`/api/super-psf/events/${eventId}/invites`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ invites }),
  });
  return parseOrThrow(res, 'Failed to add consumers');
}

export async function sendEventInvite(eventId: number, inviteId: number): Promise<{ ok: boolean; invitationSentAt: string; personalizedLinkSentAt: string; uuid: string; fullUrl: string; name: string; mobile: string }> {
  const res = await signedFetch(`/api/super-psf/events/${eventId}/invites/${inviteId}/send`, { method: 'POST' });
  return parseOrThrow(res, 'Failed to send invitation');
}

export async function deleteEventInvite(eventId: number, inviteId: number): Promise<{ ok: boolean }> {
  const res = await signedFetch(`/api/super-psf/events/${eventId}/invites/${inviteId}`, { method: 'DELETE' });
  return parseOrThrow(res, 'Failed to remove invite');
}

export async function fetchMe(): Promise<{ name: string; photo?: string; role?: string; email?: string; mobile?: string; ceaNumber?: string; identifier?: string }> {
  const res = await signedFetch('/api/super-psf/user/me');
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw Object.assign(new Error(err?.error || 'Unauthorized'), { code: err?.code });
  }
  return res.json();
}

export async function recordFilterApplication(mode: 'COMPARE_TRANSACTIONS' | 'BENCHMARK_NEW_LAUNCH', filters: unknown): Promise<void> {
  const res = await publicFilterHistoryFetch({ method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ mode, filters }) });
  parseOrThrow(res, 'Failed to save filter application');
}
